import { useEffect } from 'react';
import { SITE_URL, DEFAULT_OG_IMAGE, getSEOForPath, isNoIndexPath } from '../config/seo';
import { blogsData } from '../data/blogsData';

/** Build breadcrumb list for pathname for JSON-LD */
function getBreadcrumbItems(pathname) {
  const path = (pathname || '/').replace(/\/$/, '') || '/';
  const segments = path.split('/').filter(Boolean);
  const items = [{ name: 'Home', url: SITE_URL }];
  let acc = '';
  const labels = {
    website: 'Website Packages',
    pricing: 'Pricing',
    contact: 'Contact Us',
    faq: 'FAQ',
    services: 'Services',
    products: 'Products',
    referral: 'Referral',
    legal: 'Legal',
    purchase: 'Purchase',
    essential: 'Essential Package',
    professional: 'Professional Package',
    'business-plus': 'Business Plus Package',
    'business-elite': 'Business Elite Package',
    enterprise: 'Enterprise Package',
    terms: 'Terms & Conditions',
    privacy: 'Privacy Policy',
    'refund-policy': 'Refund Policy',
    'service-delivery-policy': 'Service Delivery Policy',
    about: 'About Us',
    disclaimer: 'Disclaimer',
    'pricing-payment-policy': 'Pricing & Payment Policy',
    blogs: 'Blog',
    careers: 'Careers',
  };
  segments.forEach((seg) => {
    acc += `/${seg}`;
    items.push({ name: labels[seg] || seg.replace(/-/g, ' '), url: `${SITE_URL}${acc}` });
  });
  return items;
}

/** Inject a JSON-LD script into head */
function injectJsonLd(id, data) {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo', id);
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

/**
 * Updates document title, meta description, canonical, OG, Twitter, and JSON-LD on route change.
 * Sets noindex for private/dashboard routes. Injects Organization, WebSite, and BreadcrumbList schema.
 */
export function useSEO(pathname) {
  useEffect(() => {
    if (!pathname) return;

    // Clean up previous SEO scripts
    document.querySelectorAll('script[type="application/ld+json"][data-seo]').forEach((s) => s.remove());

    const noindex = isNoIndexPath(pathname);
    const seo = getSEOForPath(pathname, blogsData);
    const canonicalPath = pathname === '/' ? '' : pathname.replace(/\/$/, '');
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    const ogImage = seo.ogImage || DEFAULT_OG_IMAGE;
    const ogImageAlt = seo.ogImageAlt || 'ApkaKhata - Business Solutions';

    // Set Title
    document.title = seo.title;

    // Set Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', seo.description);

    // Set Robots
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!metaRobots) {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        document.head.appendChild(metaRobots);
      }
      metaRobots.setAttribute('content', 'noindex, nofollow');
    } else if (metaRobots) {
      metaRobots.setAttribute('content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }

    // Set Keywords
    if (seo.keywords) {
      let metaKw = document.querySelector('meta[name="keywords"]');
      if (!metaKw) {
        metaKw = document.createElement('meta');
        metaKw.setAttribute('name', 'keywords');
        document.head.appendChild(metaKw);
      }
      metaKw.setAttribute('content', seo.keywords);
    }

    // Set Canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', canonicalUrl);

    // OG Tags
    const ogProps = [
      ['og:url', canonicalUrl],
      ['og:title', seo.title],
      ['og:description', seo.description],
      ['og:image', ogImage],
      ['og:image:alt', ogImageAlt],
      ['og:type', pathname.startsWith('/blogs/') ? 'article' : 'website'],
    ];
    ogProps.forEach(([prop, content]) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', prop);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    });

    // Twitter Tags
    const twitterProps = [
      ['twitter:url', canonicalUrl],
      ['twitter:title', seo.title],
      ['twitter:description', seo.description],
      ['twitter:image', ogImage],
      ['twitter:image:alt', ogImageAlt],
    ];
    twitterProps.forEach(([name, content]) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    });

    // JSON-LD Structured Data
    if (!noindex) {
      // 1. Organization
      injectJsonLd('organization', {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: 'ApkaKhata',
        url: SITE_URL,
        logo: `${SITE_URL}/logo/ak_official.webp`,
        description: 'Professional website development, business management software, and AI solutions.',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'Customer Service',
          email: 'info@apkakhata.com',
          url: `${SITE_URL}/contact`,
        },
      });

      // 2. WebSite
      injectJsonLd('website', {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'ApkaKhata',
        url: SITE_URL,
        description: 'Complete business solutions: website development, hotel & restaurant systems, AI solutions.',
        publisher: { '@id': `${SITE_URL}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/website?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
      });

      // 3. Breadcrumbs
      const breadcrumbItems = getBreadcrumbItems(pathname);
      if (breadcrumbItems.length > 1) {
        injectJsonLd('breadcrumb', {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbItems.map((item, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: item.name,
            item: item.url,
          })),
        });
      }

      // 4. Article Schema (for dynamic blogs)
      if (pathname.startsWith('/blogs/') && seo.blog) {
        const blog = seo.blog;
        injectJsonLd('article', {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: blog.title,
          description: blog.excerpt,
          image: ogImage,
          author: {
            '@type': 'Organization', // Or 'Person' if blog.author is a name
            name: blog.author || 'ApkaKhata Team',
          },
          publisher: { '@id': `${SITE_URL}/#organization` },
          datePublished: blog.date || new Date().toISOString(),
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': canonicalUrl,
          },
        });
      }
    }
  }, [pathname]);
}
